export const transformToBackendPayload = (flowData) => {
  const { name, nodes, edges, status } = flowData;

  // Find the start node to get trigger event
  const startNode = nodes.find(n => n.type === 'startNode');
  const endNodes = nodes.filter(n => n.type === 'endNode');
  const triggerEvent = startNode?.data.eventName || 'manual';

  // Get all non-start/end nodes
  const regularNodes = nodes.filter(n => n.type !== 'startNode' && n.type !== 'endNode');

  // Create a map of node IDs to their index in the FULL array (including start)
  const nodeIdToIndex = {};

  if (startNode) {
    nodeIdToIndex[startNode.id] = 0;
  }

  regularNodes.forEach((node, index) => {
    nodeIdToIndex[node.id] = index + 1;
  });

  endNodes.forEach((endNode, index) => {
    nodeIdToIndex[endNode.id] = regularNodes.length + 1 + index;
  });

  // Build the transformed nodes array
  const transformedNodes = [];
  let config = {
    trigger_type: startNode?.data.triggerType || 'manual',
    trigger_contract_fields: startNode?.data.triggerContractFields || [],
    trigger_sample_json: startNode?.data.triggerSampleJson || '',
  };

  if (startNode?.data.triggerType === 'api') {
    config = {
      ...config,
      api_auth_method: startNode.data.apiAuthMethod || 'bearer',
    };
  }

  if (startNode?.data.triggerType === 'event') {
    config = {
      ...config,
      event_name: startNode.data.eventName || '',
      event_trigger_mode: startNode.data.eventTriggerMode || 'new_event',
      eventProperties: startNode.data.eventProperties || [],
    };
  }

  if (startNode?.data.triggerType === 'customer_segment') {
    config = {
      ...config,
      customerSegmentId: startNode.data.customerSegmentId || '',
      segmentEntryMode: startNode.data.segmentEntryMode || 'existing',
      segmentExitOnMatch: startNode.data.segmentExitOnMatch || false,
      customerCount: startNode.data.customerCount || 0,
      allCustomers: startNode.data.allCustomers || false,
      segmentName: startNode.data.segmentName || '',
      enableSchedule: startNode.data.enableSchedule || false,
      ...(startNode.data.enableSchedule && {
        scheduleType: startNode.data.scheduleType || 'daily',
        scheduleTime: startNode.data.scheduleTime || '09:00',
        scheduleTimezone: startNode.data.scheduleTimezone || 'Asia/Kolkata',
        ...(startNode.data.scheduleType === 'weekly' && {
          scheduleDayOfWeek: startNode.data.scheduleDayOfWeek || 'monday',
        }),
        ...(startNode.data.scheduleType === 'monthly' && {
          scheduleDayOfMonth: startNode.data.scheduleDayOfMonth || '1',
        }),
      }),
    };
  }

  if (startNode) {
    transformedNodes.push({
      type: 'start',
      name: startNode.data.label || 'Campaign Start',
      order: 1,
      mode: 0,
      position: {
        x: startNode.position.x,
        y: startNode.position.y,
      },
      width: 240,
      height: 120,
      selected: false,
      positionAbsolute: {
        x: startNode.position.x,
        y: startNode.position.y
      },
      dragging: false,
      config,
      next_node_index: (() => {
        const startEdge = edges.find(e => e.source === startNode?.id);
        if (startEdge) {
          return nodeIdToIndex[startEdge.target];
        }
        return regularNodes.length > 0 ? 1 : nodeIdToIndex[endNodes[0]?.id];
      })()
    });
  }

  regularNodes.forEach((node, index) => {
    transformedNodes.push(
      transformNode(node, edges, nodeIdToIndex, regularNodes, index + 2, endNodes)
    );
  });

  endNodes.forEach((endNode, index) => {
    transformedNodes.push({
      type: 'end',
      name: endNode.data.label || `End ${index + 1}`,
      order: regularNodes.length + 2 + index,
      mode: 0,
      position: {
        x: endNode.position.x,
        y: endNode.position.y,
      },
      width: 240,
      height: 120,
      selected: false,
      positionAbsolute: {
        x: endNode.position.x,
        y: endNode.position.y
      },
      dragging: false,
      config: {},
    });
  });

  return {
    name: name || 'Untitled Campaign',
    description: generateDescription(transformedNodes.filter(n => n.mode === 1)),
    trigger_event: convertEventNameToSnakeCase(triggerEvent),
    status: status || 'draft',
    nodes: transformedNodes,
  };
};

/**
 * Transform individual node to backend format
 */
const transformNode = (node, edges, nodeIdToIndex, allNodes, order, endNodes) => {
  const baseNode = {
    type: getBackendNodeType(node.type),
    name: node.data.label || 'Untitled Step',
    order: order,
    mode: 1,
    position: {
      x: node.position.x,
      y: node.position.y,
    },
    width: 240,
    height: 120,
    selected: false,
    positionAbsolute: {
      x: node.position.x,
      y: node.position.y
    },
    dragging: false,
  };

  const convertWaitTimeToSeconds = (time, unit) => {
    const conversions = {
      minutes: 60,
      hours: 3600,
      days: 86400,
    };
    return time * (conversions[unit] || 3600);
  };

  switch (node.type) {
    case 'actionNode':
      const actionNode = {
        ...baseNode,
        config: {
          channel: node.data.channel?.toString() || '1',
          template_id: node.data.templateId?.toString() || '1',
          variable_mappings: node.data.variableMappings || {},
          message_type: node.data.messageType || 'template',
          message: node.data.messageType === 'template' ? (node.data.template || '') : (node.data.freeformMessage || ''),
        },
      };

      const actionEdge = edges.find(e => e.source === node.id && !e.sourceHandle);
      if (actionEdge) {
        actionNode.next_node_index = nodeIdToIndex[actionEdge.target];
      }

      return actionNode;

    case 'delayNode':
      const delayNode = {
        ...baseNode,
        config: {
          duration_seconds: convertDurationToSeconds(
            node.data.duration || '1',
            node.data.unit || 'days'
          ),
        },
      };

      const delayEdge = edges.find(e => e.source === node.id && !e.sourceHandle);
      if (delayEdge) {
        delayNode.next_node_index = nodeIdToIndex[delayEdge.target];
      }

      return delayNode;

    case 'conditionNode':
      return {
        ...baseNode,
        config: transformConditionConfig(node, edges, nodeIdToIndex, endNodes),
      };

    // =====================================================
    // ENHANCED API NODE — Updated with all new fields
    // =====================================================
    case 'apiNode': {
      // Build custom_headers object from headers array OR legacy customHeaders string
      let customHeaders = {};
      if (node.data.headers && Array.isArray(node.data.headers)) {
        customHeaders = node.data.headers
          .filter(h => h.key && h.key.trim() !== '')
          .reduce((acc, h) => ({ ...acc, [h.key]: h.value }), {});
      } else if (node.data.customHeaders) {
        customHeaders = parseJSON(node.data.customHeaders) || {};
      }

      // Build query_params array (only non-empty keys)
      const queryParams = (node.data.queryParams || [])
        .filter(p => p.key && p.key.trim() !== '')
        .map(p => ({ key: p.key, value: p.value }));

      // Build response_mapping array (only non-empty fields)
      const responseMapping = (node.data.responseMapping || [])
        .filter(m => m.responseField && m.responseField.trim() !== '')
        .map(m => ({ response_field: m.responseField, save_as: m.saveAs }));

      // Parse request payload
      const rawPayload = node.data.apiPayload || node.data.payload || '';
      const requestPayload = parseJSON(rawPayload) || rawPayload || {};

      const executionMode = node.data.executionMode || 'sync';

      const apiNodeConfig = {
        // Basic
        step_name: node.data.label || 'API Call',
        http_method: node.data.apiMethod || node.data.method || 'GET',
        api_endpoint: node.data.apiEndpoint || node.data.endpoint || '',

        // Authentication
        auth_type: node.data.authType || 'none',
        api_key: node.data.apiKey || '',
        auth_header_key: node.data.authHeaderKey || 'X-API-Key',
        bearer_token: node.data.bearerToken || '',
        basic_username: node.data.basicUsername || '',
        basic_password: node.data.basicPassword || '',

        // Execution mode
        execution_mode: executionMode,
        timeout: node.data.timeout || 30,
        response_key: node.data.responseKey || 'api_response',

        // Query parameters
        query_params: queryParams,

        // Headers
        custom_headers: customHeaders,

        // Body
        body_type: node.data.bodyType || 'json',
        request_payload: requestPayload,

        // Response handling
        response_mapping: responseMapping,
        handle_4xx: node.data.handle4xx || false,
        handle_5xx: node.data.handle5xx || false,

        // Retry configuration
        retry_enabled: node.data.retryEnabled || false,
        retry_attempts: node.data.retryAttempts || 3,
        retry_strategy: node.data.retryStrategy || 'exponential',

        // Response contract / sample
        response_namespace: node.data.responseNamespace || 'api_response',
        contract_fields: node.data.contractFields || [],
        sample_json: node.data.sampleJson || '',
      };

      const apiNode = {
        ...baseNode,
        config: apiNodeConfig,
      };

      // ===== BRANCHING LOGIC =====
      if (executionMode === 'sync') {
        // Sync mode: two branches — success (2xx) and error (4xx/5xx)
        const successEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'success');
        const errorEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'error');

        // Success branch
        if (successEdge) {
          const targetIndex = nodeIdToIndex[successEdge.target];
          const isEndNode = endNodes.some(en => en.id === successEdge.target);
          apiNode.config.on_success = isEndNode ? 'end' : 'continue';
          apiNode.config.success_branch_index = targetIndex;
        } else {
          apiNode.config.on_success = 'end';
          apiNode.config.success_branch_index = nodeIdToIndex[endNodes[0]?.id] ?? -1;
        }

        // Error branch
        if (errorEdge) {
          const targetIndex = nodeIdToIndex[errorEdge.target];
          const isEndNode = endNodes.some(en => en.id === errorEdge.target);
          apiNode.config.on_error = isEndNode ? 'end' : 'continue';
          apiNode.config.error_branch_index = targetIndex;
        } else {
          apiNode.config.on_error = 'end';
          apiNode.config.error_branch_index = nodeIdToIndex[endNodes[0]?.id] ?? -1;
        }
      } else {
        // Async mode: single next node (fire & continue)
        const asyncEdge = edges.find(e => e.source === node.id && !e.sourceHandle);
        if (asyncEdge) {
          apiNode.next_node_index = nodeIdToIndex[asyncEdge.target];
        }
      }

      return apiNode;
    }
    case 'abTestNode':
      return transformABTestNode(node, edges, nodeIdToIndex, baseNode, endNodes);

    case 'messageReadNode':
      return transformMessageReadNode(node, edges, nodeIdToIndex, baseNode, endNodes);

    case 'customerProfileNode': {
      const rawConditions =
        node.data.conditions && node.data.conditions.length > 0
          ? node.data.conditions
          : node.data.profileKey
            ? [
              {
                profile_key: node.data.profileKey,
                operator: node.data.operator || 'equals',
                match_value: node.data.matchValue || '',
              },
            ]
            : [];

      const conditions = rawConditions
        .filter((c) => c.profile_key && c.profile_key.trim() !== '')
        .map((c) => ({
          profile_key: c.profile_key,
          operator: c.operator || 'equals',
          match_value:
            c.operator === 'is_empty' || c.operator === 'is_not_empty'
              ? ''
              : c.match_value || '',
        }));

      const config = {
        step_name: node.data.label || 'Customer Profile Check',
        conditions: conditions,
        logic: 'AND',
        true_label: node.data.trueLabel || 'Match Found (True)',
        false_label: node.data.falseLabel || 'No Match (False)',
        on_match: 'continue',
        on_no_match: 'continue',
      };

      const trueEdge = edges.find(
        (e) => e.source === node.id && e.sourceHandle === 'true'
      );
      if (trueEdge) {
        config.yes_branch_index = nodeIdToIndex[trueEdge.target];
        config.on_match = 'continue';
      } else {
        config.yes_branch_index = nodeIdToIndex[endNodes[0]?.id] ?? -1;
        config.on_match = 'end';
      }

      const falseEdge = edges.find(
        (e) => e.source === node.id && e.sourceHandle === 'false'
      );
      if (falseEdge) {
        config.no_branch_index = nodeIdToIndex[falseEdge.target];
        config.on_no_match = 'continue';
      } else {
        config.no_branch_index = nodeIdToIndex[endNodes[0]?.id] ?? -1;
        config.on_no_match = 'end';
      }

      return { ...baseNode, config };
    }

    case 'checkChannelConsentNode': {
      const config = {
        step_name: node.data.label || 'Check Channel Consent',
        channel: node.data.channel?.toString() || '',
        channel_label: node.data.channelLabel || '',
        enabled_label: node.data.enabledLabel || 'Enabled',
        disabled_label: node.data.disabledLabel || 'Disabled',
        on_enabled: 'continue',
        on_disabled: 'continue',
      };

      const enabledEdge = edges.find(
        (e) => e.source === node.id && e.sourceHandle === 'enabled'
      );
      if (enabledEdge) {
        config.enabled_branch_index = nodeIdToIndex[enabledEdge.target];
        config.on_enabled = endNodes.some(en => en.id === enabledEdge.target) ? 'end' : 'continue';
      } else {
        config.enabled_branch_index = nodeIdToIndex[endNodes[0]?.id] ?? -1;
        config.on_enabled = 'end';
      }

      const disabledEdge = edges.find(
        (e) => e.source === node.id && e.sourceHandle === 'disabled'
      );
      if (disabledEdge) {
        config.disabled_branch_index = nodeIdToIndex[disabledEdge.target];
        config.on_disabled = endNodes.some(en => en.id === disabledEdge.target) ? 'end' : 'continue';
      } else {
        config.disabled_branch_index = nodeIdToIndex[endNodes[0]?.id] ?? -1;
        config.on_disabled = 'end';
      }

      return { ...baseNode, config };
    }

    case 'updateChannelConsentNode': {
      const updateConsentNode = {
        ...baseNode,
        config: {
          step_name: node.data.label || 'Update Channel Consent',
          channel: node.data.channel?.toString() || '',
          channel_label: node.data.channelLabel || '',
          consent_action: node.data.consentAction || '',
        },
      };

      const updateConsentEdge = edges.find(e => e.source === node.id && !e.sourceHandle);
      if (updateConsentEdge) {
        updateConsentNode.next_node_index = nodeIdToIndex[updateConsentEdge.target];
      }

      return updateConsentNode;
    }

    case 'updateProfileNode':
      const updateProfileNode = {
        ...baseNode,
        config: {
          tag_name: node.data.tagName || '',
        },
      };

      const updateProfileEdge = edges.find(e => e.source === node.id && !e.sourceHandle);
      if (updateProfileEdge) {
        updateProfileNode.next_node_index = nodeIdToIndex[updateProfileEdge.target];
      }

      return updateProfileNode;

    case 'dynamicDelayNode': {
      const delaySubtype = node.data.delaySubtype || 'dynamic';

      const config = {
        delay_type: delaySubtype,
        duration: node.data.duration || '1',
        unit: node.data.unit || 'days',
      };

      if (delaySubtype !== 'time') {
        config.dynamic_wait_source = node.data.dynamicWaitSource || 'event';

        if (node.data.dynamicWaitSource === 'event') {
          config.event_field_path =
            node.data.dynamicEventFieldPath || 'event.delivery.resume_at';
        }

        if (node.data.dynamicWaitSource === 'api') {
          config.api_node_id = node.data.dynamicApiNode || '';
          config.api_response_field = node.data.dynamicApiField || 'resume_at';
        }

        if (node.data.dynamicWaitSource === 'expression') {
          config.expression = node.data.dynamicExpression || '';
        }

        config.fallback_duration_seconds = convertDurationToSeconds(
          node.data.fallbackDuration || '1',
          node.data.fallbackUnit || 'days'
        );

        config.max_wait_duration_seconds = convertDurationToSeconds(
          node.data.maxWaitDuration || '30',
          node.data.maxWaitUnit || 'days'
        );
      }

      const dynamicDelayNode = {
        ...baseNode,
        config,
      };

      const dynamicDelayEdge = edges.find(
        (e) => e.source === node.id && !e.sourceHandle
      );
      if (dynamicDelayEdge) {
        dynamicDelayNode.next_node_index =
          nodeIdToIndex[dynamicDelayEdge.target];
      }

      return dynamicDelayNode;
    }

    case 'buttonClickedNode': {
      const buttons = node.data.buttons || [];
      const activeButtons = buttons.filter(btn => btn.name && btn.name.trim() !== '');

      const buttonBranches = activeButtons.map((button, index) => {
        const buttonBranch = {
          button_id: button.id,
          button_name: button.name,
        };

        if (button.conditionEnabled) {
          const trueEdge = edges.find(e => e.source === node.id && e.sourceHandle === `${button.id}_true`);
          const falseEdge = edges.find(e => e.source === node.id && e.sourceHandle === `${button.id}_false`);

          let trueBranchIndex = -1;
          let falseBranchIndex = -1;

          if (trueEdge) {
            trueBranchIndex = nodeIdToIndex[trueEdge.target];
          } else {
            trueBranchIndex = nodeIdToIndex[endNodes[0]?.id];
          }

          if (falseEdge) {
            falseBranchIndex = nodeIdToIndex[falseEdge.target];
          } else {
            falseBranchIndex = nodeIdToIndex[endNodes[0]?.id];
          }

          buttonBranch.condition_enabled = true;
          buttonBranch.condition_time = button.conditionTime || 60;
          buttonBranch.condition_unit = button.conditionUnit || 'minutes';
          buttonBranch.condition_reference = button.conditionReference || 'message_sent';
          buttonBranch.condition_seconds = convertWaitTimeToSeconds(
            button.conditionTime || 60,
            button.conditionUnit || 'minutes'
          );
          buttonBranch.true_branch_index = trueBranchIndex;
          buttonBranch.false_branch_index = falseBranchIndex;
        } else {
          const edge = edges.find(e => e.source === node.id && e.sourceHandle === `${button.id}_clicked`);

          let branchIndex = -1;
          if (edge) {
            branchIndex = nodeIdToIndex[edge.target];
          } else {
            branchIndex = nodeIdToIndex[endNodes[0]?.id];
          }

          buttonBranch.condition_enabled = false;
          buttonBranch.branch_index = branchIndex;
        }

        return buttonBranch;
      });

      const buttonClickedNode = {
        ...baseNode,
        config: {
          step_name: node.data.label || 'Button Clicked',
          buttons: buttonBranches,
        },
      };

      if (node.data.noClickWaitEnabled) {
        buttonClickedNode.config.no_click_enabled = true;
        buttonClickedNode.config.no_click_time = node.data.noClickWaitTime || 60;
        buttonClickedNode.config.no_click_unit = node.data.noClickWaitUnit || 'minutes';
        buttonClickedNode.config.no_click_reference = node.data.noClickWaitReference || 'message_sent';
        buttonClickedNode.config.no_click_seconds = convertWaitTimeToSeconds(
          node.data.noClickWaitTime || 60,
          node.data.noClickWaitUnit || 'minutes'
        );

        const noClickEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'no_click');
        if (noClickEdge) {
          buttonClickedNode.config.no_click_branch_index = nodeIdToIndex[noClickEdge.target];
        } else {
          buttonClickedNode.config.no_click_branch_index = nodeIdToIndex[endNodes[0]?.id];
        }
      }

      return buttonClickedNode;
    }

    case 'dtmfNode': {
      const dtmfResponses = node.data.dtmfResponses || [];
      const activeDTMF = dtmfResponses.filter(resp => resp.key && resp.key.trim() !== '');

      const dtmfBranches = activeDTMF.map((response, index) => {
        const responseBranch = {
          dtmf_key: response.key,
          label: response.label,
          response_id: response.id,
        };

        const edge = edges.find(e => e.source === node.id && e.sourceHandle === `${response.id}_received`);

        if (edge) {
          responseBranch.branch_index = nodeIdToIndex[edge.target];
        } else {
          responseBranch.branch_index = nodeIdToIndex[endNodes[0]?.id];
        }

        if (response.timeoutEnabled) {
          responseBranch.timeout_enabled = true;
          responseBranch.timeout_duration = response.timeoutDuration || 30;
          responseBranch.timeout_unit = response.timeoutUnit || 'seconds';
          responseBranch.timeout_seconds = convertWaitTimeToSeconds(
            response.timeoutDuration || 30,
            response.timeoutUnit || 'seconds'
          );
        }

        return responseBranch;
      });

      const dtmfNode = {
        ...baseNode,
        config: {
          step_name: node.data.label || 'DTMF Response',
          dtmf_responses: dtmfBranches,
        },
      };

      if (node.data.noResponseWaitEnabled) {
        dtmfNode.config.no_response_enabled = true;
        dtmfNode.config.no_response_time = node.data.noResponseWaitTime || 30;
        dtmfNode.config.no_response_unit = node.data.noResponseWaitUnit || 'seconds';
        dtmfNode.config.no_response_seconds = convertWaitTimeToSeconds(
          node.data.noResponseWaitTime || 30,
          node.data.noResponseWaitUnit || 'seconds'
        );

        const noResponseEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'no_response');
        if (noResponseEdge) {
          dtmfNode.config.no_response_branch_index = nodeIdToIndex[noResponseEdge.target];
        } else {
          dtmfNode.config.no_response_branch_index = nodeIdToIndex[endNodes[0]?.id];
        }
      }

      return dtmfNode;
    }

    case 'enhancedConditionNode': {
      const config = {
        condition_type: node.data.conditionType || 'conversion',
      };

      if (node.data.conditionType === 'conversion') {
        config.conversion_definition = node.data.conversionDefinition || '';
        config.match_entity_by = node.data.matchEntityBy || 'order_id';
      } else if (node.data.conditionType === 'journey') {
        config.journey_state = node.data.journeyState || '';
      } else if (node.data.conditionType === 'profile') {
        config.attribute_source = node.data.attributeSource || 'profile';
        config.field_selector = node.data.fieldSelector || '';
        config.operator = node.data.profileOperator || 'equals';
        config.value = node.data.profileValue || '';
      } else if (node.data.conditionType === 'count') {
        config.event_name = convertEventNameToSnakeCase(node.data.eventName || '');
        config.count_operator = node.data.countOperator || '>';
        config.count_value = parseInt(node.data.countValue) || 0;
      }

      const yesEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'yes');
      const noEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'no');

      if (yesEdge) {
        config.yes_branch_index = nodeIdToIndex[yesEdge.target];
      } else {
        config.yes_branch_index = nodeIdToIndex[endNodes[0]?.id];
      }

      if (noEdge) {
        config.no_branch_index = nodeIdToIndex[noEdge.target];
      } else {
        config.no_branch_index = nodeIdToIndex[endNodes[0]?.id];
      }

      return {
        ...baseNode,
        config,
      };
    }

    case 'smsInboundNode': {
      const smsResponses = node.data.smsResponses || [];
      const activeResponses = smsResponses.filter(resp => resp.key && resp.key.trim() !== '');

      const smsBranches = activeResponses.map((response) => {
        const responseBranch = {
          sms_key: response.key,
          label: response.label,
          response_id: response.id,
        };

        const edge = edges.find(e => e.source === node.id && e.sourceHandle === `${response.id}_received`);
        if (edge) {
          responseBranch.branch_index = nodeIdToIndex[edge.target];
        } else {
          responseBranch.branch_index = nodeIdToIndex[endNodes[0]?.id];
        }

        if (response.timeoutEnabled) {
          responseBranch.timeout_enabled = true;
          responseBranch.timeout_duration = response.timeoutDuration || 30;
          responseBranch.timeout_unit = response.timeoutUnit || 'seconds';
          responseBranch.timeout_seconds = convertWaitTimeToSeconds(
            response.timeoutDuration || 30,
            response.timeoutUnit || 'seconds'
          );
        }

        return responseBranch;
      });

      const smsInboundNode = {
        ...baseNode,
        config: {
          step_name: node.data.label || 'SMS Inbound',
          sms_responses: smsBranches,
        },
      };

      if (node.data.noResponseWaitEnabled) {
        smsInboundNode.config.no_response_enabled = true;
        smsInboundNode.config.no_response_time = node.data.noResponseWaitTime || 24;
        smsInboundNode.config.no_response_unit = node.data.noResponseWaitUnit || 'hours';
        smsInboundNode.config.no_response_seconds = convertWaitTimeToSeconds(
          node.data.noResponseWaitTime || 24,
          node.data.noResponseWaitUnit || 'hours'
        );

        const noResponseEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'no_response');
        if (noResponseEdge) {
          smsInboundNode.config.no_response_branch_index = nodeIdToIndex[noResponseEdge.target];
        } else {
          smsInboundNode.config.no_response_branch_index = nodeIdToIndex[endNodes[0]?.id];
        }
      }

      return smsInboundNode;
    }

    case 'smartConditionNode': {
      const groups = node.data.groups || [];

      const groupBranches = groups.map((group) => {
        const rules = (group.rules || [])
          .filter((r) => r.variable || r.variablePath)
          .map((r) => ({
            variable_path: r.variablePath || r.variable,
            variable_type: r.variableType || 'string',
            operator: r.operator || 'equals',
            value: r.value || '',
          }));

        const groupEdge = edges.find(
          (e) => e.source === node.id && e.sourceHandle === `group-${group.id}`
        );

        return {
          id: group.id,
          label: group.label,
          color: group.color || 'blue',
          logic: 'AND',
          rules,
          branch_index: groupEdge
            ? nodeIdToIndex[groupEdge.target]
            : nodeIdToIndex[endNodes[0]?.id] ?? -1,
        };
      });

      const defaultEdge = edges.find(
        (e) => e.source === node.id && e.sourceHandle === 'default'
      );

      return {
        ...baseNode,
        config: {
          step_name: node.data.label || 'Smart Condition',
          groups: groupBranches,
          default_label: node.data.defaultLabel || 'No Match',
          default_branch_index: defaultEdge
            ? nodeIdToIndex[defaultEdge.target]
            : nodeIdToIndex[endNodes[0]?.id] ?? -1,
        },
      };
    }

   case 'ivrNode': {
  const ivrNode = {
    ...baseNode,
    config: {
      template_id: node.data.templateId || null,
      template_name: node.data.templateName || '',
      voice_script: node.data.voiceScript || '',
      enable_retries: node.data.enableRetries || false,
      // { varName: { sourceType: 'fixed'|'flow_context', sourceValue: '...', eventName: '', propertyName: '', fallbackValue: '' } }
      variable_mappings: node.data.ivrVariableMappings || {},
    },
  };

  if (node.data.enableRetries) {
    ivrNode.config.no_of_retries = node.data.noOfRetries || 3;
    ivrNode.config.wait_time_between_retries = node.data.waitTimeBetweenRetries || 5;
    ivrNode.config.wait_time_unit = node.data.waitTimeUnit || 'minutes';
    ivrNode.config.wait_time_seconds = convertWaitTimeToSeconds(
      node.data.waitTimeBetweenRetries || 5,
      node.data.waitTimeUnit || 'minutes'
    );
  }

  const ivrEdge = edges.find(e => e.source === node.id && !e.sourceHandle);
  if (ivrEdge) {
    ivrNode.next_node_index = nodeIdToIndex[ivrEdge.target];
  }

  return ivrNode;
}
    case 'callFlowNode': {
      const callFlowNode = {
        ...baseNode,
        config: {
          flow_id: node.data.selectedFlowId || '',
          flow_name: node.data.selectedFlowName || '',
          flow_status: node.data.flowStatus || '',
        },
      };
      const callFlowEdge = edges.find(e => e.source === node.id && !e.sourceHandle);
      if (callFlowEdge) {
        callFlowNode.next_node_index = nodeIdToIndex[callFlowEdge.target];
      }
      return callFlowNode;
    }

    case 'callWhatsappFlowNode': {
      const kvPairs = (node.data.kvPairs || [])
        .filter(p => p.key && p.key.trim() !== '')
        .map(p => ({ key: p.key, value: p.value || '' }));

      const whatsappFlowConfig = {
        flow_id: node.data.flowId || '',
        body_text: node.data.bodyText || '',
        parameters: kvPairs,
        timeout_enabled: node.data.timeoutEnabled || false,
      };

      if (node.data.timeoutEnabled) {
        whatsappFlowConfig.timeout_time = node.data.timeoutWaitTime || 30;
        whatsappFlowConfig.timeout_unit = node.data.timeoutWaitUnit || 'minutes';
        whatsappFlowConfig.timeout_reference = node.data.timeoutWaitReference || 'flow_trigger';
        whatsappFlowConfig.timeout_seconds = convertWaitTimeToSeconds(
          node.data.timeoutWaitTime || 30,
          node.data.timeoutWaitUnit || 'minutes'
        );
      }

      const whatsappFlowNode = { ...baseNode, config: whatsappFlowConfig };

      const completedEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'completed');
      const timeoutEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'timeout');

      if (completedEdge) {
        whatsappFlowNode.config.completed_branch_index = nodeIdToIndex[completedEdge.target];
      } else {
        whatsappFlowNode.config.completed_branch_index = nodeIdToIndex[endNodes[0]?.id] ?? -1;
      }

      if (node.data.timeoutEnabled) {
        whatsappFlowNode.config.timeout_branch_index = timeoutEdge
          ? nodeIdToIndex[timeoutEdge.target]
          : nodeIdToIndex[endNodes[0]?.id] ?? -1;
      }

      return whatsappFlowNode;
    }

    case 'multiStepNode': {
      const stepCount = node.data.stepCount || 3;
      const stepNames = node.data.stepNames || [];

      const steps = Array.from({ length: stepCount }, (_, i) => {
        const edge = edges.find(e => e.source === node.id && e.sourceHandle === `step-${i}`);
        return {
          step_index: i,
          label: stepNames[i] || `Step ${i + 1}`,
          branch_index: edge
            ? nodeIdToIndex[edge.target]
            : nodeIdToIndex[endNodes[0]?.id] ?? -1,
        };
      });

      return {
        ...baseNode,
        config: {
          step_name: node.data.label || 'Multi Step',
          step_count: stepCount,
          step_names: stepNames,
          steps,
        },
      };
    }

    case 'mergeNode': {
      const inputCount = node.data.inputCount || 3;
      const branches = Array.from({ length: inputCount }, (_, i) => {
        const edge = edges.find(e => e.target === node.id && e.targetHandle === `merge-in-${i}`);
        return {
          branch_index: i,
          source_node_index: edge ? nodeIdToIndex[edge.source] : -1,
          source_handle: edge ? (edge.sourceHandle || null) : null,
        };
      });

      const outEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'merge-out');

      return {
        ...baseNode,
        config: {
          step_name: node.data.label || 'Merge Branches',
          input_count: inputCount,
          merge_behavior: node.data.mergeBehavior || 'any',
          continue_once: node.data.continueOnce !== false,
          timeout_enabled: node.data.timeoutEnabled || false,
          timeout_value: node.data.timeoutValue || 30,
          timeout_unit: node.data.timeoutUnit || 'minutes',
          continue_on_timeout: node.data.continueOnTimeout || false,
          branches,
        },
        next_node_index: outEdge ? nodeIdToIndex[outEdge.target] : nodeIdToIndex[endNodes[0]?.id] ?? -1,
      };
    }

    default:
      return baseNode;
  }
};

const transformABTestNode = (node, edges, nodeIdToIndex, baseNode, endNodes) => {
  const numBranches = node.data.numBranches || 2;
  const branches = node.data.branches || { A: 50, B: 50 };

  const variants = [];

  for (let i = 0; i < numBranches; i++) {
    const branchLabel = String.fromCharCode(65 + i);
    const branchLabelLower = branchLabel.toLowerCase();
    const percentage = branches[branchLabel] || Math.floor(100 / numBranches);

    const edge = edges.find(e => e.source === node.id && e.sourceHandle === branchLabelLower);

    let branchIndex = -1;
    if (edge) {
      branchIndex = nodeIdToIndex[edge.target];
    } else {
      branchIndex = nodeIdToIndex[endNodes[0]?.id];
    }

    variants.push({
      id: branchLabel,
      name: `Variant ${branchLabel}`,
      percentage: percentage,
      branch_index: branchIndex,
    });
  }

  return {
    ...baseNode,
    config: {
      step_name: node.data.label || 'A/B Test',
      num_branches: numBranches,
      variants: variants,
    },
    next_node_index: -1,
  };
};

const transformMessageReadNode = (node, edges, nodeIdToIndex, baseNode, endNodes) => {
  const trackingMode = node.data.trackingMode || 'read';
  const characterLimit = trackingMode === 'read' ? 300 : 200;

  const config = {
    tracking_mode: trackingMode,
    message_status: characterLimit,
    wait_time: node.data.waitTime || 24,
    wait_unit: node.data.waitUnit || 'hours',
    wait_seconds: convertWaitTimeToSeconds(node.data.waitTime || 24, node.data.waitUnit || 'hours'),
  };

  const yesEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'yes');
  const noEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'no');

  if (yesEdge) {
    const targetIndex = nodeIdToIndex[yesEdge.target];
    const isEndNode = endNodes.some(endNode => endNode.id === yesEdge.target);

    if (isEndNode) {
      config.on_success = 'end';
      config.yes_branch_index = targetIndex;
    } else {
      config.on_success = 'continue';
      config.yes_branch_index = targetIndex;
    }
  } else {
    config.on_success = 'end';
    config.yes_branch_index = nodeIdToIndex[endNodes[0]?.id];
  }

  if (noEdge) {
    const targetIndex = nodeIdToIndex[noEdge.target];
    const isEndNode = endNodes.some(endNode => endNode.id === noEdge.target);

    if (isEndNode) {
      config.on_failure = 'end';
      config.no_branch_index = targetIndex;
    } else {
      config.on_failure = 'continue';
      config.no_branch_index = targetIndex;
    }
  } else {
    config.on_failure = 'end';
    config.no_branch_index = nodeIdToIndex[endNodes[0]?.id];
  }

  return {
    ...baseNode,
    config,
  };
};

const transformConditionConfig = (node, edges, nodeIdToIndex, endNodes) => {
  const config = {
    event_name: convertEventNameToSnakeCase(node.data.eventSource || ''),
    fallback_enabled: node.data.fallbackEnabled || false,
  };

  if (node.data.conditionLogic === 'within_time' || node.data.conditionLogic === 'not_occurred') {
    config.lookback_seconds = convertDurationToSeconds(
      node.data.timeWindowValue || '1',
      node.data.timeWindowUnit || 'days'
    );
  }

  if (node.data.conditionLogic === 'count_of_events') {
    config.count_condition = {
      operator: node.data.countOperator || 'equals',
      value: parseInt(node.data.countValue) || 0,
    };
  }

  if (node.data.conditionLogic === 'attribute_check') {
    config.attribute_check = {
      attribute_name: node.data.attributeName || '',
      operator: node.data.operator || 'equals',
      attribute_value: node.data.attributeValue || '',
    };
  }

  if (node.data.enableAttributeMatching && node.data.matchAttribute) {
    config.attribute_matching = {
      target_attribute: node.data.matchAttribute,
      operator: node.data.comparisonType || 'equals',
      compare_against: getCompareAgainstType(node.data.referenceType),
      on_missing_attribute: node.data.attributeFallbackEnabled ? 'no_path' : 'continue',
    };

    if (node.data.referenceType === 'previous_event') {
      const [refEvent, refAttr] = (node.data.previousEventRef || '').split('.');
      config.attribute_matching.reference_event = convertEventNameToSnakeCase(refEvent || '');
      config.attribute_matching.reference_attribute = refAttr || '';
    } else if (node.data.referenceType === 'message_variable') {
      config.attribute_matching.message_variable = node.data.messageVariable || '';
    } else if (node.data.referenceType === 'static') {
      config.attribute_matching.fixed_value = node.data.staticValue || '';
    }
  }

  const yesEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'yes');
  const noEdge = edges.find(e => e.source === node.id && e.sourceHandle === 'no');

  if (yesEdge) {
    const targetIndex = nodeIdToIndex[yesEdge.target];
    const isEndNode = endNodes.some(endNode => endNode.id === yesEdge.target);

    if (isEndNode) {
      config.on_match = 'end';
      config.yes_branch_index = targetIndex;
    } else {
      config.on_match = 'continue';
      config.yes_branch_index = targetIndex;
    }
  } else {
    config.on_match = 'end';
    config.yes_branch_index = nodeIdToIndex[endNodes[0]?.id];
  }

  if (noEdge) {
    const targetIndex = nodeIdToIndex[noEdge.target];
    const isEndNode = endNodes.some(endNode => endNode.id === noEdge.target);

    if (isEndNode) {
      config.on_no_match = 'end';
      config.no_branch_index = targetIndex;
    } else {
      config.on_no_match = 'continue';
      config.no_branch_index = targetIndex;
    }
  } else {
    config.on_no_match = 'end';
    config.no_branch_index = nodeIdToIndex[endNodes[0]?.id];
  }

  return config;
};

const getBackendNodeType = (frontendType) => {
  const typeMap = {
    actionNode: 'action',
    delayNode: 'wait',
    dynamicDelayNode: 'dynamic_wait',
    conditionNode: 'check_event',
    apiNode: 'api_call',
    abTestNode: 'ab_test',
    messageReadNode: 'message_read',
    updateProfileNode: 'update_profile',
    buttonClickedNode: 'button_clicked',
    dtmfNode: 'dtmf_response',
    enhancedConditionNode: 'enhanced_condition',
    ivrNode: 'ivr_call',
    customerProfileNode: 'customer_profile',
    smsInboundNode: 'sms_inbound',
    smartConditionNode: 'smart_condition',
    callFlowNode: 'call_flow',
    callWhatsappFlowNode: 'whatsapp_call_flow',
    multiStepNode: 'multi_step',
    mergeNode: 'merge',
    checkChannelConsentNode: 'check_channel_consent',
    updateChannelConsentNode: 'update_channel_consent',
  };
  return typeMap[frontendType] || 'action';
};

const convertDurationToSeconds = (duration, unit) => {
  const value = parseInt(duration) || 1;
  const multipliers = {
    seconds: 1,
    minutes: 60,
    hours: 3600,
    days: 86400,
    weeks: 604800,
  };
  return value * (multipliers[unit] || 86400);
};

const convertWaitTimeToSeconds = (waitTime, waitUnit) => {
  const value = parseInt(waitTime) || 1;
  const multipliers = {
    minutes: 60,
    hours: 3600,
    days: 86400,
  };
  return value * (multipliers[waitUnit] || 3600);
};

const convertEventNameToSnakeCase = (eventName) => {
  if (!eventName) return '';
  return eventName
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
};

const getCompareAgainstType = (referenceType) => {
  const typeMap = {
    previous_event: 'previous_event_attribute',
    message_variable: 'message_variable',
    static: 'static_value',
  };
  return typeMap[referenceType] || 'static_value';
};

const parseJSON = (jsonString) => {
  if (!jsonString) return null;
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    return null;
  }
};

const generateDescription = (nodes) => {
  const actionCount = nodes.filter(n => n.type === 'action').length;
  const delayCount = nodes.filter(n => n.type === 'wait').length;
  const conditionCount = nodes.filter(n => n.type === 'check_event').length;
  const apiCount = nodes.filter(n => n.type === 'api_call').length;
  const abTestCount = nodes.filter(n => n.type === 'ab_test').length;
  const messageReadCount = nodes.filter(n => n.type === 'message_read').length;
  const updateProfileCount = nodes.filter(n => n.type === 'update_profile').length;

  const parts = [];
  if (actionCount > 0) parts.push(`${actionCount} message${actionCount > 1 ? 's' : ''}`);
  if (delayCount > 0) parts.push(`${delayCount} wait step${delayCount > 1 ? 's' : ''}`);
  if (conditionCount > 0) parts.push(`${conditionCount} condition${conditionCount > 1 ? 's' : ''}`);
  if (apiCount > 0) parts.push(`${apiCount} API call${apiCount > 1 ? 's' : ''}`);
  if (abTestCount > 0) parts.push(`${abTestCount} A/B test${abTestCount > 1 ? 's' : ''}`);
  if (messageReadCount > 0) parts.push(`${messageReadCount} message read check${messageReadCount > 1 ? 's' : ''}`);
  if (updateProfileCount > 0) parts.push(`${updateProfileCount} profile update${updateProfileCount > 1 ? 's' : ''}`);

  return parts.length > 0 ? `Automated flow with ${parts.join(', ')}` : 'Automated flow';
};